import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Area,
  Legend,
} from "recharts";
import { useEffect } from "react";

type Props = {
  mediaHist: number;
  sigma: number;
  meta: number;
  onUpdateResultados?: (res: {
    riscoAbaixo: number;
    riscoAcima: number;
    riscoSeguro: number;
  }) => void;
};

// CDF da normal padrão
function cdf(z: number) {
  return (1 + erf(z / Math.sqrt(2))) / 2;
}

// Função de erro aproximada
function erf(x: number) {
  const a1 = 0.254829592,
    a2 = -0.284496736,
    a3 = 1.421413741,
    a4 = -1.453152027,
    a5 = 1.061405429,
    p = 0.3275911;
  const sign = x < 0 ? -1 : 1;
  const absX = Math.abs(x);
  const t = 1 / (1 + p * absX);
  const y =
    1 -
    (((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t) *
      Math.exp(-absX * absX);
  return sign * y;
}

// PDF da normal
function pdf(x: number, media: number, sigma: number) {
  return (
    (1 / (sigma * Math.sqrt(2 * Math.PI))) *
    Math.exp(-0.5 * Math.pow((x - media) / sigma, 2))
  );
}

// Gera curva com áreas separadas
function gerarCurva(media: number, sigma: number) {
  const dados = [];
  const step = sigma / 10;
  const min = media - 4 * sigma;
  const max = media + 4 * sigma;

  for (let x = min; x <= max; x += step) {
    const densidade = pdf(x, media, sigma);
    dados.push({
      peso: parseFloat(x.toFixed(1)),
      densidade,
      abaixo95: x <= 95 ? densidade : 0,
      seguro: x > 95 && x <= 100 ? densidade : 0,
      acima100: x > 100 ? densidade : 0,
    });
  }
  return dados;
}

export default function CurvaRisco({
  mediaHist,
  sigma,
  meta,
  onUpdateResultados,
}: Props) {
  const dados = gerarCurva(meta, sigma);

  // Calcula riscos com base no CDF
  const riscoAbaixo = cdf((95 - meta) / sigma) * 100;
  const riscoAcima = (1 - cdf((100 - meta) / sigma)) * 100;
  const riscoSeguro = 100 - riscoAbaixo - riscoAcima;

  useEffect(() => {
    if (onUpdateResultados) {
      onUpdateResultados({ riscoAbaixo, riscoAcima, riscoSeguro });
    }
  }, [meta, sigma]);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
      <h4 className="font-semibold mb-2">📊 Curva de Risco</h4>
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={dados}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="peso" />
          <YAxis />


          {/* Áreas coloridas */}
          <Area
            type="monotone"
            dataKey="abaixo95"
            name="Risco de Multa"
            stroke="red"
            fill="red"
            opacity={0.6}
            baseValue={0}
          />
          <Area
            type="monotone"
            dataKey="seguro"
            name="Zona Segura"
            stroke="green"
            fill="green"
            opacity={0.6}
            baseValue={0}
          />
          <Area
            type="monotone"
            dataKey="acima100"
            name="Excesso"
            stroke="orange"
            fill="orange"
            opacity={0.6}
            baseValue={0}
          />

          {/* Curva principal */}
          <Line
            type="monotone"
            dataKey="densidade"
            stroke="#0ea5e9"
            strokeWidth={2}
            dot={false}
            name="Curva"
            legendType="none" // não aparece na legenda
          />

          {/* Linha da média histórica */}
          <ReferenceLine
            x={mediaHist}
            stroke="purple"
            strokeDasharray="2 2"
            label={`Hist ${mediaHist.toFixed(1)}g`}
          />

          {/* Linha da meta */}
          <ReferenceLine
            x={meta}
            stroke="blue"
            label={`Meta ${meta.toFixed(1)}g`}
          />

          {/* Legenda automática */}
          <Legend />
        </ComposedChart>
      </ResponsiveContainer>

      {/* 📌 Legenda manual com os percentuais corretos */}
      <div className="mt-3 text-sm text-slate-700 space-y-1">
        <p>🔴 Risco de multa (&lt;95g): <b>{riscoAbaixo.toFixed(2)}%</b></p>
        <p>🟢 Zona segura (95.1–100g): <b>{riscoSeguro.toFixed(2)}%</b></p>
        <p>🟠 Excesso (&gt;100g): <b>{riscoAcima.toFixed(2)}%</b></p>
      </div>
    </div>
  );
}
